import { create } from 'zustand';
import { ChemFile, Molecule, SearchResult } from '../types/chemistry';
import { chemistryApi } from '../api/chemistry';

// File-level search types (cross-file search on FilesPage)
interface FileSearchResult {
  id: number;
  fileName: string;
  matchCount: number;
  totalMolecules: number;
  createdAt?: string;
  modifiedAt?: string;
}

interface FileSearchResponse {
  results: FileSearchResult[];
  totalFiles: number;
  totalMatches: number;
  query: string;
  searchType: string;
}

interface ChemistryStore {
  // Files state
  files: ChemFile[];
  selectedFileIds: string[];
  lockedFileIds: string[];  // Files currently being processed (merge, export, etc.)
  isLoadingFiles: boolean;
  filesError: string | null;

  // Current file state
  currentFile: ChemFile | null;
  molecules: Molecule[];
  totalMolecules: number;
  isLoadingMolecules: boolean;
  moleculesError: string | null;

  // In-file search state (within EditorPage)
  searchResults: SearchResult | null;
  isSearching: boolean;
  _searchGen: number;  // generation counter to discard stale search results

  // Cross-file search state (FilesPage - persists across navigation)
  fileSearchQuery: string | null;
  fileSearchType: 'substructure' | 'similarity' | 'exact' | null;
  fileSearchResponse: FileSearchResponse | null;

  // Actions - Files
  fetchFiles: () => Promise<void>;
  fetchLockedFiles: () => Promise<void>;
  setFiles: (files: ChemFile[]) => void;
  setSelectedFileIds: (ids: string[]) => void;
  toggleFileSelection: (id: string) => void;
  selectAllFiles: () => void;
  clearSelection: () => void;
  deleteSelectedFiles: () => Promise<void>;

  // Actions - Current file
  setCurrentFile: (file: ChemFile | null) => void;
  fetchFileInfo: (fileId: string) => Promise<void>;  // File metadata only (for SSRM)
  fetchMolecules: (fileId: string, page?: number, limit?: number) => Promise<void>;

  // Actions - In-file search
  searchStructure: (
    fileId: string,
    molfile: string,
    searchType: 'substructure' | 'similarity' | 'exact',
    params?: {
      threshold?: number;
      stereoMode?: 'strict' | 'ignore';
      tautomerMode?: 'exact' | 'inchi' | 'rsmarts';
      resonance?: boolean;
    }
  ) => Promise<void>;
  clearSearch: () => void;

  // Actions - Cross-file search
  setFileSearchResults: (
    query: string,
    searchType: 'substructure' | 'similarity' | 'exact',
    response: FileSearchResponse
  ) => void;
  clearFileSearch: () => void;
}

export const useChemistryStore = create<ChemistryStore>((set, get) => ({
  // Initial state
  files: [],
  selectedFileIds: [],
  lockedFileIds: [],
  isLoadingFiles: false,
  filesError: null,

  currentFile: null,
  molecules: [],
  totalMolecules: 0,
  isLoadingMolecules: false,
  moleculesError: null,

  searchResults: null,
  isSearching: false,
  _searchGen: 0,

  // Cross-file search state (persists across navigation)
  fileSearchQuery: null,
  fileSearchType: null,
  fileSearchResponse: null,

  // File actions
  fetchFiles: async () => {
    set({ isLoadingFiles: true, filesError: null });
    try {
      const files = await chemistryApi.getFiles();
      set({ files, isLoadingFiles: false });
    } catch (error: any) {
      set({
        filesError: error.message || 'Failed to fetch files',
        isLoadingFiles: false
      });
    }
  },

  fetchLockedFiles: async () => {
    try {
      const lockedFileIds = await chemistryApi.getLockedFiles();
      set({ lockedFileIds });
    } catch (error) {
      // Silently fail - locked files are not critical
      console.warn('Failed to fetch locked files:', error);
    }
  },

  setFiles: (files) => set({ files }),

  setSelectedFileIds: (ids) => set({ selectedFileIds: ids }),

  toggleFileSelection: (id) => {
    const { selectedFileIds } = get();
    if (selectedFileIds.includes(id)) {
      set({ selectedFileIds: selectedFileIds.filter((fid) => fid !== id) });
    } else {
      set({ selectedFileIds: [...selectedFileIds, id] });
    }
  },

  selectAllFiles: () => {
    const { files } = get();
    set({ selectedFileIds: files.map((f) => f.id) });
  },

  clearSelection: () => set({ selectedFileIds: [] }),

  deleteSelectedFiles: async () => {
    const { selectedFileIds, fetchFiles } = get();
    if (selectedFileIds.length === 0) return;

    try {
      await chemistryApi.deleteFiles(selectedFileIds);
      set({ selectedFileIds: [] });
      await fetchFiles();
    } catch (error: any) {
      set({ filesError: error.message || 'Failed to delete files' });
    }
  },

  // Current file actions
  setCurrentFile: (file) => set({ currentFile: file }),

  // Fetch file metadata only (for SSRM mode - no molecules loaded)
  fetchFileInfo: async (fileId) => {
    console.log('[Store] fetchFileInfo START - fileId:', fileId);
    // Reset totalMolecules to 0 to ensure SSRM is used during loading (prevents race condition)
    set({ isLoadingMolecules: true, moleculesError: null, molecules: [], totalMolecules: 0 });
    try {
      console.log('[Store] fetchFileInfo - calling API...');
      const file = await chemistryApi.getFile(fileId);
      console.log('[Store] fetchFileInfo - API returned:', {
        originalFileName: file.originalFileName,
        fileName: file.fileName,
        id: file.id
      });
      const count = file.moleculesLibraryCount;
      // For small files (≤1000), keep isLoadingMolecules=true — fetchMolecules will
      // clear it after loading client-side data. This prevents a flash of "No molecules
      // found" between fetchFileInfo completing and fetchMolecules starting.
      // For large files (SSRM) and empty files, set false so the grid can render.
      const needsClientLoad = count > 0 && count <= 1000;
      set({
        currentFile: file,
        totalMolecules: count,
        isLoadingMolecules: needsClientLoad
      });
      console.log('[Store] fetchFileInfo - state updated with new currentFile, keepLoading:', needsClientLoad);
    } catch (error: any) {
      console.error('[Store] fetchFileInfo ERROR:', error);
      set({
        moleculesError: error.message || 'Failed to fetch file info',
        isLoadingMolecules: false
      });
    }
  },

  fetchMolecules: async (fileId, page = 1, limit = 100) => {
    console.log('[Store] fetchMolecules called:', { fileId, page, limit });
    set({ isLoadingMolecules: true, moleculesError: null });
    try {
      const file = await chemistryApi.getFile(fileId);
      const { molecules, total } = await chemistryApi.getMolecules(fileId, page, limit);
      console.log('[Store] fetchMolecules received:', { moleculesCount: molecules.length, total, fileCount: file.moleculesLibraryCount });
      set({
        currentFile: file,
        molecules,
        totalMolecules: total,
        isLoadingMolecules: false
      });
      console.log('[Store] fetchMolecules done, store molecules:', get().molecules.length);
    } catch (error: any) {
      console.error('[Store] fetchMolecules error:', error);
      set({
        moleculesError: error.message || 'Failed to fetch molecules',
        isLoadingMolecules: false
      });
    }
  },

  // Search actions
  searchStructure: async (fileId, molfile, searchType, params) => {
    console.log('[chemistryStore] searchStructure called with:', { fileId, molfileLen: molfile?.length, searchType, params });
    const gen = get()._searchGen + 1;
    set({ isSearching: true, _searchGen: gen });
    try {
      const results = await chemistryApi.searchStructure(fileId, molfile, searchType, params);
      // Discard results if search was cleared/superseded while in flight
      if (get()._searchGen !== gen) return;
      console.log('[chemistryStore] searchStructure results:', results);
      set({ searchResults: results, isSearching: false });
    } catch (error: any) {
      if (get()._searchGen !== gen) return;
      console.error('[chemistryStore] searchStructure error:', error);
      set({ isSearching: false });
      throw error;
    }
  },

  clearSearch: () => set((s) => ({ searchResults: null, isSearching: false, _searchGen: s._searchGen + 1 })),

  // Cross-file search actions
  setFileSearchResults: (query, searchType, response) => set({
    fileSearchQuery: query,
    fileSearchType: searchType,
    fileSearchResponse: response,
  }),

  clearFileSearch: () => set({
    fileSearchQuery: null,
    fileSearchType: null,
    fileSearchResponse: null,
  }),
}));
